/*  BlogManager — Admin → Blog
    ────────────────────────────────────────────────────────────────
    iter100.153 — Marketing team surface for authoring blog posts.
    Two views inside one route:

      • List    — every post with status pill + quick actions
      • Editor  — markdown source + live preview + all metadata

    Media Library integrates for hero images so admins never manually
    upload from disk here. Author is a hybrid picker: dropdown of
    existing staff + a "Custom author" option.
*/
import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Plus, MagnifyingGlass, PencilSimple, Archive, ArrowClockwise,
    CheckCircle, ArrowLeft, FloppyDisk, Rocket,
    Eye, Image as ImageIcon, X, Sparkle,
    ClockClockwise, WarningCircle, ThumbsUp, ThumbsDown,
    Newspaper,
} from "@phosphor-icons/react";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";


/* Empty post shell used when creating a new draft. Matches the shape
   the POST /api/admin/blog endpoint expects. */
const EMPTY_POST = {
    id: null,
    title: "",
    slug: "",
    excerpt: "",
    content_md: "",
    hero_image_url: "",
    hero_image_alt: "",
    category: "smile-science",
    audience: "patient",
    author_type: "staff",
    author_staff_id: "",
    author_name: "",
    author_role: "",
    author_avatar_url: "",
    featured: false,
    status: "draft",
    seo_title: "",
    seo_description: "",
    seo_og_image: "",
};


export default function BlogManager() {
    const [view, setView] = useState("list");     // "list" | "editor"
    const [selectedId, setSelectedId] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const openEditor = (id) => {
        setSelectedId(id);
        setView("editor");
    };
    const backToList = () => {
        setSelectedId(null);
        setView("list");
        setRefreshKey((k) => k + 1);
    };

    return (
        <div data-testid="admin-blog-manager">
            {view === "list" ? (
                <BlogList key={refreshKey} onOpen={openEditor} onNew={() => openEditor(null)} />
            ) : (
                <BlogEditor postId={selectedId} onBack={backToList} />
            )}
        </div>
    );
}


/* Canonical StatTile — matches Patients/Referrals/ScanVisits grammar
   exactly (items-start, label above value, bg-card border, active ring). */
function StatTile({ label, value, Icon, tone = "primary", onClick, active, testid }) {
    const accent = tone === "emerald" ? "bg-emerald-100 text-emerald-700"
        : tone === "amber" ? "bg-amber-100 text-amber-700"
        : tone === "rose" ? "bg-rose-100 text-rose-700"
        : tone === "slate" ? "bg-slate-100 text-slate-700"
        : tone === "violet" ? "bg-violet-100 text-violet-700"
        : "bg-primary/10 text-primary";
    const Component = onClick ? "button" : "div";
    return (
        <Component
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`flex items-start gap-3 rounded-xl bg-card border p-3 text-left transition ${
                active ? "border-primary ring-1 ring-primary/30 bg-primary/5"
                       : (onClick ? "border-border hover:border-primary/40 hover:shadow-sm cursor-pointer" : "border-border")
            }`}
            aria-pressed={onClick ? active : undefined}
            data-testid={testid}
        >
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1">{value}</div>
            </div>
        </Component>
    );
}


/* ================================================================
   List view
   ================================================================ */
function BlogList({ onOpen, onNew }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [q, setQ] = useState("");
    const [decisionFor, setDecisionFor] = useState(null); // { id, title } for reject modal

    const load = async () => {
        setLoading(true);
        try {
            /* iter101.32 — Fetch ALL posts and filter client-side so the
               KPI strip counts stay accurate as the chip filter changes. */
            const { data } = await api.get("/admin/blog");
            setItems(data.items || []);
        } catch (err) {
            toast.error("Failed to load posts");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const stats = useMemo(() => {
        const s = { total: items.length, under_review: 0, published: 0, draft: 0, rejected: 0, archived: 0 };
        items.forEach((p) => { if (p.status && s[p.status] !== undefined) s[p.status] += 1; });
        return s;
    }, [items]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return items.filter((p) => {
            if (statusFilter !== "all" && p.status !== statusFilter) return false;
            if (!term) return true;
            return (p.title || "").toLowerCase().includes(term)
                || (p.excerpt || "").toLowerCase().includes(term)
                || (p.author_name || "").toLowerCase().includes(term);
        });
    }, [items, q, statusFilter]);

    const handleAction = async (id, action) => {
        try {
            if (action === "delete") {
                if (!window.confirm("Hard-delete this post? This cannot be undone.")) return;
                await api.delete(`/admin/blog/${id}`);
            } else {
                await api.post(`/admin/blog/${id}/${action}`);
            }
            toast.success(`Post ${action}d`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || `Couldn't ${action} post`);
        }
    };

    const handleApprove = async (id) => {
        try {
            await api.post(`/admin/blog-review/posts/${id}/decision`, {
                decision: "approve",
                feedback: "",
            });
            toast.success("Approved and published");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not approve");
        }
    };

    const submitReject = async (id, feedback) => {
        try {
            await api.post(`/admin/blog-review/posts/${id}/decision`, {
                decision: "reject",
                feedback,
            });
            toast.success("Rejected with feedback");
            setDecisionFor(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not reject");
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Content · Blog</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5" data-testid="admin-blog-title">
                        Blog Manager
                    </h1>
                    <p className="text-xs text-muted-foreground mt-1">Author, publish, and manage every article across /blog.</p>
                </div>
                <Button size="sm" onClick={onNew} className="rounded-full" data-testid="admin-blog-new">
                    <Plus size={14} weight="bold" className="mr-1.5" /> New Post
                </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="admin-blog-kpi-strip">
                <StatTile label="Total posts"   value={stats.total}        Icon={Newspaper}      tone="primary"  onClick={() => setStatusFilter("all")}          active={statusFilter === "all"}          testid="admin-blog-kpi-total" />
                <StatTile label="Under review"  value={stats.under_review} Icon={ClockClockwise} tone="amber"    onClick={() => setStatusFilter("under_review")} active={statusFilter === "under_review"} testid="admin-blog-kpi-review" />
                <StatTile label="Published"     value={stats.published}    Icon={CheckCircle}    tone="emerald"  onClick={() => setStatusFilter("published")}    active={statusFilter === "published"}    testid="admin-blog-kpi-published" />
                <StatTile label="Drafts"        value={stats.draft}        Icon={PencilSimple}   tone="slate"    onClick={() => setStatusFilter("draft")}        active={statusFilter === "draft"}        testid="admin-blog-kpi-draft" />
                <StatTile label="Archived"      value={stats.archived}     Icon={Archive}        tone="rose"     onClick={() => setStatusFilter("archived")}     active={statusFilter === "archived"}     testid="admin-blog-kpi-archived" />
            </div>

            <FilterSearchBar
                query={q}
                setQuery={setQ}
                placeholder="Search title, author, excerpt…"
                searchTestid="admin-blog-search"
                testid="admin-blog-search-bar"
            />

            <FilterChipRow
                testid="admin-blog-status-chips"
                chipTestidPrefix="admin-blog-status"
                value={statusFilter}
                onChange={setStatusFilter}
                chips={[
                    { id: "all",          label: "All",          count: stats.total,        tone: "primary" },
                    { id: "under_review", label: "Under review", count: stats.under_review, tone: "amber" },
                    { id: "published",    label: "Published",    count: stats.published,    tone: "emerald" },
                    { id: "draft",        label: "Drafts",       count: stats.draft,        tone: "slate" },
                    { id: "rejected",     label: "Rejected",     count: stats.rejected,     tone: "rose" },
                    { id: "archived",     label: "Archived",     count: stats.archived,     tone: "rose" },
                ]}
            />

            <div className="bg-card border border-border rounded-2xl overflow-hidden bento-shadow">
                {loading ? (
                    <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center">
                        <Sparkle size={32} weight="duotone" className="mx-auto text-primary/40 mb-3" />
                        <div className="font-semibold">No posts yet</div>
                        <p className="text-sm text-muted-foreground mt-1">Click New Post to write your first article.</p>
                    </div>
                ) : (                    <table className="w-full text-sm" data-testid="admin-blog-table">
                        <thead className="bg-secondary/60">
                            <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">
                                <th className="px-4 py-3">Title</th>
                                <th className="px-4 py-3">Category</th>
                                <th className="px-4 py-3">Audience</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Author</th>
                                <th className="px-4 py-3">Updated</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {filtered.map((p) => (
                                <tr key={p.id} className="hover:bg-secondary/30 transition-colors" data-testid={`admin-blog-row-${p.slug}`}>
                                    <td className="px-4 py-3">
                                        <button
                                            type="button"
                                            onClick={() => onOpen(p.id)}
                                            className="text-left font-semibold text-foreground hover:text-primary transition-colors"
                                        >
                                            {p.featured && <Sparkle size={11} weight="fill" className="inline text-amber-500 mr-1.5" />}
                                            {p.title}
                                        </button>
                                        <div className="text-[11px] text-muted-foreground mt-0.5 truncate max-w-md">/blog/{p.slug}</div>
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs">{p.category}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${p.audience === "professional" ? "bg-amber-100 text-amber-800" : "bg-primary/10 text-primary"}`}>
                                            {p.audience}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <StatusPill status={p.status} />
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs">{p.author_name || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs">
                                        {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex gap-1">
                                            <IconBtn title="Edit" testId={`admin-blog-edit-${p.slug}`} onClick={() => onOpen(p.id)}>
                                                <PencilSimple size={14} />
                                            </IconBtn>
                                            {p.status === "under_review" && (
                                                <>
                                                    <IconBtn title="Approve & publish" testId={`admin-blog-approve-${p.slug}`} onClick={() => handleApprove(p.id)}>
                                                        <ThumbsUp size={14} weight="fill" />
                                                    </IconBtn>
                                                    <IconBtn title="Reject with feedback" testId={`admin-blog-reject-${p.slug}`} onClick={() => setDecisionFor({ id: p.id, title: p.title })}>
                                                        <ThumbsDown size={14} />
                                                    </IconBtn>
                                                </>
                                            )}
                                            {p.status === "published" && (
                                                <IconBtn title="Archive" testId={`admin-blog-archive-${p.slug}`} onClick={() => handleAction(p.id, "archive")}>
                                                    <Archive size={14} />
                                                </IconBtn>
                                            )}
                                            {p.status === "draft" && (
                                                <IconBtn title="Publish now (bypass review)" testId={`admin-blog-publish-${p.slug}`} onClick={() => handleAction(p.id, "publish")}>
                                                    <Rocket size={14} />
                                                </IconBtn>
                                            )}
                                            {(p.status === "archived" || p.status === "rejected") && (
                                                <IconBtn title="Restore to draft" testId={`admin-blog-restore-${p.slug}`} onClick={() => handleAction(p.id, "restore")}>
                                                    <ArrowClockwise size={14} />
                                                </IconBtn>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {decisionFor && (
                <RejectModal
                    postTitle={decisionFor.title}
                    onClose={() => setDecisionFor(null)}
                    onConfirm={(fb) => submitReject(decisionFor.id, fb)}
                />
            )}
        </div>
    );
}


function RejectModal({ postTitle, onClose, onConfirm }) {
    const [feedback, setFeedback] = useState("");
    const [busy, setBusy] = useState(false);
    return (
        <div className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm grid place-items-center p-4">
            <div className="bg-card rounded-3xl w-full max-w-md p-6 shadow-2xl" data-testid="admin-blog-reject-modal">
                <div className="flex items-start gap-3 mb-5">
                    <div className="w-11 h-11 rounded-full bg-rose-50 grid place-items-center">
                        <ThumbsDown size={20} weight="fill" className="text-rose-600" />
                    </div>
                    <div>
                        <div className="font-display font-bold text-lg leading-tight">Reject &ldquo;{postTitle}&rdquo;</div>
                        <div className="text-xs text-muted-foreground mt-1">The author will see your feedback and can revise the post.</div>
                    </div>
                </div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Feedback for the author</Label>
                <Textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Tell the author what needs to change before this can be published…"
                    rows={5}
                    className="mt-1"
                    autoFocus
                    data-testid="admin-blog-reject-feedback"
                />
                <div className="flex gap-2 mt-5 justify-end">
                    <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
                    <Button
                        onClick={async () => { setBusy(true); await onConfirm(feedback); setBusy(false); }}
                        disabled={busy || !feedback.trim()}
                        className="rounded-full bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                        data-testid="admin-blog-reject-confirm"
                    >
                        <ThumbsDown size={13} weight="fill" className="mr-1.5" />
                        Send rejection
                    </Button>
                </div>
            </div>
        </div>
    );
}


function StatusPill({ status }) {
    const map = {
        published:    { icon: CheckCircle,   tone: "bg-emerald-100 text-emerald-700",  label: "Published" },
        draft:        { icon: PencilSimple,  tone: "bg-secondary text-foreground/70",  label: "Draft" },
        under_review: { icon: ClockClockwise,tone: "bg-amber-100 text-amber-800",      label: "Under review" },
        rejected:     { icon: WarningCircle, tone: "bg-rose-100 text-rose-700",        label: "Rejected" },
        archived:     { icon: Archive,       tone: "bg-secondary text-muted-foreground",label: "Archived" },
    };
    const cfg = map[status] || map.draft;
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cfg.tone}`}>
            <Icon size={10} weight="fill" />
            {cfg.label}
        </span>
    );
}


function IconBtn({ children, title, onClick, testId }) {
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            data-testid={testId}
            className="w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
        >
            {children}
        </button>
    );
}


/* ================================================================
   Editor view
   ================================================================ */
function BlogEditor({ postId, onBack }) {
    const [form, setForm] = useState(EMPTY_POST);
    const [loading, setLoading] = useState(Boolean(postId));
    const [saving, setSaving] = useState(false);
    const [staff, setStaff] = useState([]);
    const [showMediaPicker, setShowMediaPicker] = useState(false);

    // Load authors list once
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/blog/authors");
                setStaff(data.staff || []);
            } catch { /* non-fatal */ }
        })();
    }, []);

    // Load post if editing
    useEffect(() => {
        if (!postId) return;
        (async () => {
            try {
                const { data } = await api.get(`/admin/blog/${postId}`);
                setForm({ ...EMPTY_POST, ...data });
            } catch {
                toast.error("Failed to load post");
                onBack();
            } finally {
                setLoading(false);
            }
        })();
    }, [postId]);

    const update = (patch) => setForm((f) => ({ ...f, ...patch }));

    const handleSave = async ({ publish = false } = {}) => {
        if (!form.title.trim()) {
            toast.error("Add a title before saving");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: form.title,
                excerpt: form.excerpt,
                content_md: form.content_md,
                category: form.category,
                audience: form.audience,
                hero_image_url: form.hero_image_url,
                hero_image_alt: form.hero_image_alt,
                author_type: form.author_type,
                author_staff_id: form.author_staff_id || null,
                author_name: form.author_name,
                author_role: form.author_role,
                author_avatar_url: form.author_avatar_url,
                featured: form.featured,
                seo_title: form.seo_title,
                seo_description: form.seo_description,
                seo_og_image: form.seo_og_image,
            };
            let updated;
            if (form.id) {
                const { data } = await api.patch(`/admin/blog/${form.id}`, {
                    ...payload,
                    slug: form.slug,
                });
                updated = data;
            } else {
                const { data } = await api.post("/admin/blog", payload);
                updated = data;
                setForm({ ...EMPTY_POST, ...updated });
            }
            if (publish) {
                await api.post(`/admin/blog/${updated.id}/publish`);
                toast.success("Post published");
            } else {
                toast.success("Draft saved");
            }
            onBack();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="p-12 text-center text-muted-foreground">Loading post…</div>;
    }

    return (
        <div className="space-y-6" data-testid="admin-blog-editor">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-primary"
                    data-testid="admin-blog-back"
                >
                    <ArrowLeft size={14} weight="bold" />
                    Back to list
                </button>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => handleSave({ publish: false })}
                        disabled={saving}
                        className="rounded-full h-10 px-4"
                        data-testid="admin-blog-save-draft"
                    >
                        <FloppyDisk size={14} weight="bold" className="mr-1.5" />
                        Save draft
                    </Button>
                    <Button
                        onClick={() => handleSave({ publish: true })}
                        disabled={saving}
                        className="rounded-full h-10 px-5 bg-primary text-primary-foreground"
                        data-testid="admin-blog-publish"
                    >
                        <Rocket size={14} weight="fill" className="mr-1.5" />
                        Publish
                    </Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-[1fr_320px] gap-6">
                {/* Left column — title, content, live preview */}
                <div className="space-y-4">
                    <Input
                        value={form.title}
                        onChange={(e) => update({ title: e.target.value })}
                        placeholder="Article title"
                        className="h-14 text-2xl font-display font-bold border-0 border-b-2 border-border rounded-none bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary"
                        data-testid="admin-blog-input-title"
                    />
                    <Textarea
                        value={form.excerpt}
                        onChange={(e) => update({ excerpt: e.target.value })}
                        placeholder="Excerpt (shown on cards and social share) · aim for 1–2 sentences"
                        rows={2}
                        className="text-base bg-white border-border rounded-xl"
                        data-testid="admin-blog-input-excerpt"
                    />

                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground mb-2 flex items-center gap-1.5">
                                Markdown source
                            </div>
                            <Textarea
                                value={form.content_md}
                                onChange={(e) => update({ content_md: e.target.value })}
                                placeholder="Write your article in Markdown…&#10;&#10;## Heading&#10;Paragraph text.&#10;- List item&#10;- List item&#10;&#10;> Blockquote"
                                rows={22}
                                className="font-mono text-[13px] leading-relaxed bg-white border-border rounded-xl min-h-[500px]"
                                data-testid="admin-blog-input-content"
                            />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground mb-2 flex items-center gap-1.5">
                                <Eye size={11} /> Live preview
                            </div>
                            <div className="bg-white border border-border rounded-xl p-5 max-h-[560px] overflow-auto" data-testid="admin-blog-preview">
                                <article className="prose-invisique">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {form.content_md || "*Nothing yet — start typing on the left.*"}
                                    </ReactMarkdown>
                                </article>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right column — metadata */}
                <div className="space-y-5">
                    <Panel title="Hero image">
                        {form.hero_image_url ? (
                            <div className="space-y-3">
                                <div className="relative rounded-xl overflow-hidden bg-secondary">
                                    <img src={form.hero_image_url} alt="Hero" className="w-full h-32 object-cover" />
                                    <button
                                        type="button"
                                        onClick={() => update({ hero_image_url: "", hero_image_alt: "" })}
                                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 grid place-items-center shadow"
                                        aria-label="Remove"
                                    >
                                        <X size={13} weight="bold" />
                                    </button>
                                </div>
                                <Input
                                    value={form.hero_image_alt}
                                    onChange={(e) => update({ hero_image_alt: e.target.value })}
                                    placeholder="Alt text (accessibility)"
                                    className="text-xs h-9"
                                    data-testid="admin-blog-input-hero-alt"
                                />
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowMediaPicker(true)}
                                className="w-full h-32 rounded-xl border-2 border-dashed border-border bg-secondary/40 hover:border-primary hover:bg-primary/5 transition-colors grid place-items-center text-muted-foreground hover:text-primary"
                                data-testid="admin-blog-pick-hero"
                            >
                                <div className="text-center">
                                    <ImageIcon size={22} className="mx-auto mb-1" />
                                    <div className="text-xs font-semibold">Pick from Media Library</div>
                                </div>
                            </button>
                        )}
                    </Panel>

                    <Panel title="Category & audience">
                        <div className="space-y-3">
                            <div>
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Category</Label>
                                <Select value={form.category} onValueChange={(v) => update({ category: v })}>
                                    <SelectTrigger className="mt-1 h-10 rounded-lg" data-testid="admin-blog-select-category"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="smile-science">Smile Science</SelectItem>
                                        <SelectItem value="patient-stories">Patient Stories</SelectItem>
                                        <SelectItem value="clinical-insights">Clinical Insights</SelectItem>
                                        <SelectItem value="product-deep-dives">Product Deep-Dives</SelectItem>
                                        <SelectItem value="industry-news">Industry & News</SelectItem>
                                        <SelectItem value="aftercare">Aftercare</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Audience</Label>
                                <Select value={form.audience} onValueChange={(v) => update({ audience: v })}>
                                    <SelectTrigger className="mt-1 h-10 rounded-lg" data-testid="admin-blog-select-audience"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="patient">For Patients</SelectItem>
                                        <SelectItem value="professional">For Professionals</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center justify-between pt-2">
                                <Label className="text-sm">Featured (pin to top)</Label>
                                <Switch
                                    checked={form.featured}
                                    onCheckedChange={(v) => update({ featured: v })}
                                    data-testid="admin-blog-featured-toggle"
                                />
                            </div>
                        </div>
                    </Panel>

                    <Panel title="Author">
                        <div className="space-y-3">
                            <div className="inline-flex bg-secondary/70 rounded-full p-1 w-full">
                                {["staff", "custom"].map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => update({ author_type: t })}
                                        className={`flex-1 h-8 px-3 rounded-full text-[11px] font-bold tracking-wide uppercase transition-all ${
                                            form.author_type === t
                                                ? "bg-primary text-primary-foreground shadow-sm"
                                                : "text-foreground/60"
                                        }`}
                                        data-testid={`admin-blog-author-type-${t}`}
                                    >
                                        {t === "staff" ? "Staff" : "Custom"}
                                    </button>
                                ))}
                            </div>
                            {form.author_type === "staff" ? (
                                <Select value={form.author_staff_id || ""} onValueChange={(v) => update({ author_staff_id: v })}>
                                    <SelectTrigger className="h-10 rounded-lg" data-testid="admin-blog-select-staff">
                                        <SelectValue placeholder="Pick a staff member…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {staff.map((s) => (
                                            <SelectItem key={s.id} value={s.id}>
                                                {(s.full_name || s.name) + " · " + (s.designation || s.role)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <div className="space-y-2">
                                    <Input
                                        value={form.author_name}
                                        onChange={(e) => update({ author_name: e.target.value })}
                                        placeholder="Author name"
                                        className="h-9 text-sm"
                                        data-testid="admin-blog-input-author-name"
                                    />
                                    <Input
                                        value={form.author_role}
                                        onChange={(e) => update({ author_role: e.target.value })}
                                        placeholder="Role or title"
                                        className="h-9 text-sm"
                                    />
                                    <Input
                                        value={form.author_avatar_url}
                                        onChange={(e) => update({ author_avatar_url: e.target.value })}
                                        placeholder="Avatar URL (optional)"
                                        className="h-9 text-sm"
                                    />
                                </div>
                            )}
                        </div>
                    </Panel>

                    <Panel title="URL slug">
                        <Input
                            value={form.slug}
                            onChange={(e) => update({ slug: e.target.value })}
                            placeholder="auto-generated from title"
                            className="h-9 text-sm font-mono"
                            data-testid="admin-blog-input-slug"
                        />
                        <p className="text-[11px] text-muted-foreground mt-2">
                            /blog/<span className="font-mono">{form.slug || "auto-generated"}</span>
                        </p>
                    </Panel>

                    <Panel title="SEO">
                        <div className="space-y-2">
                            <Input
                                value={form.seo_title}
                                onChange={(e) => update({ seo_title: e.target.value })}
                                placeholder="SEO title (defaults to article title)"
                                className="h-9 text-sm"
                            />
                            <Textarea
                                value={form.seo_description}
                                onChange={(e) => update({ seo_description: e.target.value })}
                                placeholder="Meta description (<155 chars)"
                                rows={3}
                                className="text-sm"
                            />
                        </div>
                    </Panel>
                </div>
            </div>

            {showMediaPicker && (
                <MediaPicker
                    onClose={() => setShowMediaPicker(false)}
                    onPick={(m) => {
                        update({
                            hero_image_url: m.url,
                            hero_image_alt: m.alt || form.hero_image_alt || form.title,
                        });
                        setShowMediaPicker(false);
                    }}
                />
            )}
        </div>
    );
}


function Panel({ title, children }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">{title}</div>
            {children}
        </div>
    );
}


/* Lightweight media picker — pulls from the shared Media Library
   endpoint and lets the admin click one. Avoids re-implementing the
   full library UI. */
function MediaPicker({ onClose, onPick }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/media?limit=200");
                setItems(data.items || []);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return items;
        return items.filter(
            (m) =>
                (m.original_filename || "").toLowerCase().includes(term) ||
                (m.alt || "").toLowerCase().includes(term) ||
                (m.category || "").toLowerCase().includes(term),
        );
    }, [items, q]);

    return (
        <div className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm grid place-items-center p-4" data-testid="admin-blog-media-picker">
            <div className="bg-card rounded-3xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
                <div className="flex items-center justify-between p-5 border-b border-border">
                    <div>
                        <div className="font-display font-bold text-lg">Pick a hero image</div>
                        <div className="text-xs text-muted-foreground">From your Media Library · {items.length} assets</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-9 h-9 rounded-full grid place-items-center hover:bg-secondary"
                        aria-label="Close"
                    >
                        <X size={16} weight="bold" />
                    </button>
                </div>
                <div className="p-4 border-b border-border">
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by filename, alt text, or category…"
                        className="h-10"
                    />
                </div>
                <div className="flex-1 overflow-auto p-4">
                    {loading ? (
                        <div className="text-center text-muted-foreground py-12">Loading…</div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center text-muted-foreground py-12">No matches.</div>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                            {filtered.map((m) => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => onPick(m)}
                                    className="group relative aspect-square rounded-xl overflow-hidden bg-secondary border border-border hover:border-primary transition-all"
                                    data-testid={`admin-blog-media-item-${m.id}`}
                                    title={m.original_filename}
                                >
                                    <img src={m.url} alt={m.alt || m.original_filename} className="w-full h-full object-cover" loading="lazy" />
                                    <div className="absolute inset-0 bg-primary/60 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
                                        <CheckCircle size={22} weight="fill" className="text-white" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
